import { useCallback, useEffect, useRef, useState } from 'react';

const isFinished = (job) => ['COMPLETED', 'FAILED'].includes(job?.status);

const POLL_INTERVAL_MS = 2000;

/**
 * Polls one queued backend job (202 + `{ job }`) until it completes or fails.
 *
 * @param {Function} options.fetchJob - (jobId) => axios response carrying `{ job }`
 * @param {Function} options.describe - (job) => { success, message } for a COMPLETED job
 * @param {string} options.failureMessage - shown when a FAILED job doesn't explain itself
 * @param {number} options.maxWaitMs - stop chasing a job that never reports back
 * @param {Function} options.onFinished - ({ success, message, job }) once the job ends
 * @returns {{ isImporting, trackImport, job }} `job` is the latest poll, for live counts
 */
const useImportJob = ({ fetchJob, describe, failureMessage, maxWaitMs, onFinished }) => {
    const [isImporting, setIsImporting] = useState(false);
    const [job, setJob] = useState(null);

    const timeoutRef = useRef(null);
    const startedAtRef = useRef(0);
    // Latest options without restarting a running poll when callers re-render.
    const optionsRef = useRef({ fetchJob, describe, failureMessage, maxWaitMs, onFinished });
    useEffect(() => {
        optionsRef.current = { fetchJob, describe, failureMessage, maxWaitMs, onFinished };
    });

    const stop = useCallback(() => clearTimeout(timeoutRef.current), []);

    const finish = useCallback((result) => {
        setIsImporting(false);
        optionsRef.current.onFinished && optionsRef.current.onFinished(result);
    }, []);

    const poll = useCallback((jobId) => {
        timeoutRef.current = setTimeout(async () => {
            const options = optionsRef.current;
            try {
                const job = (await options.fetchJob(jobId)).data?.job;
                if (!job) return;
                setJob(job);

                if (isFinished(job)) {
                    // The backend explains its own failures.
                    if (job.status === 'FAILED') finish({ success: false, message: job.metadata?.error || options.failureMessage, job });
                    else finish({ ...options.describe(job), job });
                } else if (Date.now() - startedAtRef.current > options.maxWaitMs) {
                    finish({ success: false, message: 'The import is taking longer than expected. Check back shortly.', job });
                } else {
                    poll(jobId);
                }
            } catch (error) {
                console.error('Error checking the import', error);
                finish({ success: false, message: "Couldn't check the import status." });
            }
        }, POLL_INTERVAL_MS);
    }, [finish]);

    // Hand this the job from the 202 response to start watching it.
    const trackImport = useCallback((job) => {
        if (!job?.id) return;
        stop();
        startedAtRef.current = Date.now();
        setJob(job);
        setIsImporting(true);
        poll(job.id);
    }, [poll, stop]);

    useEffect(() => stop, [stop]);

    return { isImporting, trackImport, job };
};

// The backend reports every count as a string.
export const toCount = (value) => Number(value || 0);

export default useImportJob;
